import { useState } from 'react';

// Consistent colors for participants (same as PieChart)
export const PARTICIPANT_COLORS = [
  '#f59e0b', // ember-bright
  '#10b981', // success green
  '#3b82f6', // blue
  '#ec4899', // pink
  '#8b5cf6', // purple
  '#14b8a6', // teal
  '#f97316', // orange
  '#06b6d4', // cyan
  '#facc15', // yellow
  '#ef4444', // red
  '#84cc16', // lime
  '#a855f7', // violet
];

export function getParticipantColor(index: number): string {
  return PARTICIPANT_COLORS[index % PARTICIPANT_COLORS.length];
}

interface SessionParticipant {
  name: string;
  points: number;
  colorIndex: number;
}

interface SessionCardProps {
  sessionId: number;
  importance: number;
  invalidated: boolean;
  timestamp?: number;
  participants: SessionParticipant[];
  isSigner: boolean;
  isFinalized: boolean;
  isInvalidating: boolean;
  onInvalidate: () => void;
}

export function SessionCard({
  sessionId,
  importance,
  invalidated,
  timestamp,
  participants,
  isSigner,
  isFinalized,
  isInvalidating,
  onInvalidate,
}: SessionCardProps) {
  const [expanded, setExpanded] = useState(false);

  const formatTimestamp = (ts: number | undefined, includeTime = false) => {
    if (!ts) return '—';
    const date = new Date(ts * 1000);
    const dateStr = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (!includeTime) return dateStr;
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${dateStr} at ${timeStr}`;
  };

  const renderImportanceDots = (imp: number) => {
    return (
      <span className="importance-dots">
        {Array.from({ length: 5 }, (_, i) => (
          <span
            key={i}
            className={`importance-dot ${i < imp ? 'active' : ''}`}
          />
        ))}
      </span>
    );
  };

  // Get participants who actually participated (points > 0)
  const activeParticipants = participants.filter(p => p.points > 0);

  return (
    <div
      className={`session-card ${invalidated ? 'invalidated' : ''} ${expanded ? 'expanded' : ''}`}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Line 1: # | participant count | spacer | date */}
      <div className="session-card-line">
        <div className="session-card-line-left">
          <span className="session-number">#{sessionId + 1}</span>
          <span className="session-participant-count">
            <span className="participant-icon">👤</span> {activeParticipants.length}
          </span>
        </div>
        <span className="session-date">{formatTimestamp(timestamp)}</span>
      </div>

      {/* Line 2: importance dots | spacer | status */}
      <div className="session-card-line">
        {renderImportanceDots(importance)}
        {invalidated ? (
          <span className="session-status invalidated">Invalidated</span>
        ) : (
          <span className="session-status verified">Verified</span>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="session-card-details" onClick={(e) => e.stopPropagation()}>
          {timestamp && (
            <div className="session-card-time">
              {formatTimestamp(timestamp, true)}
            </div>
          )}
          <div className="session-card-participants-list">
            {activeParticipants.map((p, i) => (
              <div key={i} className="participant-row">
                <span
                  className="participant-color-indicator"
                  style={{ backgroundColor: getParticipantColor(p.colorIndex) }}
                />
                <span className="participant-name">{p.name}</span>
                <span className="participant-points">{p.points} pts</span>
              </div>
            ))}
          </div>

          {isSigner && !isFinalized && !invalidated && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Invalidate session ${sessionId + 1}? This cannot be undone.`)) {
                  onInvalidate();
                }
              }}
              disabled={isInvalidating}
              className="btn-secondary btn-small session-card-action"
            >
              {isInvalidating ? 'Invalidating...' : 'Invalidate'}
            </button>
          )}
        </div>
      )}

      <div className="session-card-expand-indicator">
        <span className={`expand-chevron ${expanded ? 'expanded' : ''}`}>▼</span>
      </div>
    </div>
  );
}
